import { useEffect } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useUIState } from '../contexts/UIStateContext';

/**
 * Global Import Progress Listener
 *
 * Listens for import_progress WebSocket events globally (regardless of page)
 * and updates the UIState so the ImportProgressBanner can display across all pages.
 */
export default function GlobalImportProgressListener() {
  const ws = useWebSocket();
  const { setKnowledgeImportProgress, getKnowledgeImportProgress } = useUIState();

  useEffect(() => {
    if (!ws || !ws.on) return;

    // Listen for preprocessing phase changes
    const handlePreprocessingPhase = (data) => {
      const currentProgress = getKnowledgeImportProgress();

      // Only update if we have an active import in processing status
      if (!currentProgress || currentProgress.status !== 'processing') return;

      // Update to importing (structuring) phase
      setKnowledgeImportProgress({
        ...currentProgress,
        status: 'importing',
        progress: 0,
        message: data.message,
      });
    };

    // Listen for import progress updates (from task-gen-server)
    const handleImportProgress = (data) => {
      const currentProgress = getKnowledgeImportProgress();

      // Only update if we have an active import
      if (!currentProgress) return;

      // Update progress with new data from worker
      setKnowledgeImportProgress({
        ...currentProgress,
        status: 'generating',
        progress: data.progress || 0,
        message: data.message,
        stage: data.stage,
        items_completed: data.items_completed,
        items_total: data.items_total,
        collections_completed: data.collections_completed,
        collections_total: data.collections_total,
      });
    };

    // Listen for import completion
    const handleImportCompleted = (data) => {
      const currentProgress = getKnowledgeImportProgress();

      // Only update if we have an active import
      if (!currentProgress) return;

      if (data.success) {
        // Mark as complete
        setKnowledgeImportProgress({
          ...currentProgress,
          status: 'complete',
          progress: 100,
          error: null,
          result: data.result || data,
        });
      } else {
        // Mark as error
        setKnowledgeImportProgress({
          ...currentProgress,
          status: 'error',
          progress: 0,
          error: data.error || 'Import failed',
          result: null,
        });
      }
    };

    // Register WebSocket listeners
    const cleanupPhase = ws.on('preprocessing_phase', handlePreprocessingPhase);
    const cleanupProgress = ws.on('import_progress', handleImportProgress);
    const cleanupCompleted = ws.on('import_completed', handleImportCompleted);

    return () => {
      if (cleanupPhase) cleanupPhase();
      if (cleanupProgress) cleanupProgress();
      if (cleanupCompleted) cleanupCompleted();
    };
  }, [ws, setKnowledgeImportProgress, getKnowledgeImportProgress]);

  // This component doesn't render anything
  return null;
}
